import React from 'react';

import { useQuery } from '@apollo/client';
import { Container, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import { useLocation, useParams } from 'react-router-dom';

import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import IKImage from '~/components/IKImage/IKImage';
import Loader from '~/components/Loader/Loader';
import NewsContent from '~/components/NewsItem/NewsContent';
import NewsHeader from '~/components/NewsItem/NewsHeader';
import NewsListItem from '~/components/NewsItem/NewsListItem';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import { HERO_IMG_SRC_SET } from '~/const';
import { useSsrContext } from '~/context/SsrContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { GET_LATEST_POSTS, GET_NEWS_BY_ID } from '~/graphql/news';
import { GET_RELATED_VIDEOS_POST } from '~/graphql/videos';
import useRestrictedContainer from '~/hooks/useRestrictedContainer';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useWatchList from '~/hooks/useWatchList';
import { buildInsightsRoute } from '~/routes';
import { MIDDLE_MARGIN_PX, useIsWidthUp } from '~/theme';
import { Query, QueryGetBlogPostArgs, Tenant } from '~/types';
import redirectToNewSlug from '~/utils/redirectToNewSlug';
import { toBoolean } from '~/utils/toBoolean';
import getCanonicalUrl from '~/utils/urlUtils/getCanonicalUrl';

const loadingItems = new Array(4).fill(undefined);

const NewsPage = () => {
  const { redirect } = useSsrContext();
  const { userLoading } = useUserInfoContext();
  const location = useLocation();
  const params = useParams();
  const slug = params.slug;
  const isIdInSlug = Number.isInteger(Number(slug));
  const isMobile = !useIsWidthUp('md');
  const { t } = useTenantTranslation();

  const RestrictedContainer = useRestrictedContainer();

  const {
    data: post,
    loading,
    called,
  } = useQuery<Pick<Query, 'getBlogPost'>, QueryGetBlogPostArgs>(GET_NEWS_BY_ID, {
    variables: { id: isIdInSlug ? slug : undefined, slug: isIdInSlug ? undefined : slug },
  });

  const { data: recentPostsData, loading: loadingRecentPosts } = useQuery<Pick<Query, 'listPosts'>>(
    GET_LATEST_POSTS,
    {
      variables: {
        first: 5,
        column: 'created_at',
      },
    },
  );

  const { data: relatedVideosData, loading: loadingRelatedVideos } = useQuery(
    GET_RELATED_VIDEOS_POST,
    {
      variables: {
        post_id: post?.getBlogPost?.id,
      },
      skip: !post?.getBlogPost?.id,
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  );

  const blogPost = post?.getBlogPost;
  const user = blogPost?.user;
  const postContent = blogPost?.post_content && JSON.parse(blogPost?.post_content);
  const seoTitle = blogPost?.seo_title;
  const seoDescription = blogPost?.seo_description;
  const seoImage = blogPost?.seo_image;

  const relatedVideosToDisplay = relatedVideosData?.listVideosRelatedWithPostByCategories
    ? relatedVideosData?.listVideosRelatedWithPostByCategories
    : loadingItems;

  const recentPostsToDisplay = recentPostsData?.listPosts.data
    ? recentPostsData?.listPosts.data.filter((post) => post.id !== blogPost?.id)
    : loadingItems;

  const videosIds = relatedVideosToDisplay
    ?.map((video) => video?.id)
    .filter((item): item is string => Boolean(item));
  const watchlistById = useWatchList(videosIds);
  const postVideosIds = blogPost?.videos?.map((item) => item?.id);

  if (!isIdInSlug && blogPost && slug) {
    redirectToNewSlug(redirect, location.pathname, slug, blogPost?.slug);
  }

  if (called && !loading && !blogPost) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  if (userLoading) {
    return <Loader />;
  }

  return (
    <div>
      <SeoBlock
        data={{
          seo_title: seoTitle,
          seo_description: seoDescription,
          share_image: seoImage,
        }}
        canonicalUrl={getCanonicalUrl(
          buildInsightsRoute(blogPost?.slug || blogPost?.id),
          [blogPost?.tenant].filter(toBoolean<Tenant>),
        )}
      />

      <Wrapper>
        {!loading && blogPost?.image_url && (
          <BackgroundImg
            path={blogPost?.image_url}
            draggable={false}
            srcset={HERO_IMG_SRC_SET}
            sizes="100vw"
            alt={blogPost?.title}
          />
        )}
        <NewsHeader
          categories={blogPost?.categories}
          expert={user}
          title={blogPost?.title}
          description={blogPost?.post_introduction}
          loading={loading}
          minToRead={blogPost?.time_to_read}
        />
        {!loading && (
          <>
            <Gradient />
            <TopGradient />
          </>
        )}
        <BottomGradient />
      </Wrapper>
      <NewsContent
        postVideosIds={postVideosIds}
        expert={user}
        title={blogPost?.title}
        imageUrl={blogPost?.image_url}
        postContent={postContent}
        loading={loading}
      />
      <ListWrapper>
        <StyledRestrictedContainer as={RestrictedContainer} data-testid="latestPosts">
          <Heading variant="h2" align={isMobile ? 'left' : 'center'}>
            {t('page.news.latestInsights')}
          </Heading>
        </StyledRestrictedContainer>
        <StyledContainer>
          {recentPostsToDisplay?.map((item, index) => (
            <NewsListItem key={index} data={item} type="post" loading={loadingRecentPosts} />
          ))}
        </StyledContainer>
      </ListWrapper>
      {!!relatedVideosToDisplay.length && (
        <ListWrapper isDark={true}>
          <StyledRestrictedContainer as={RestrictedContainer} data-testid="relatedVideos">
            <Heading variant="h2" align={isMobile ? 'left' : 'center'}>
              {t('page.video.relatedVideos')}
            </Heading>
          </StyledRestrictedContainer>
          <StyledContainer>
            {relatedVideosToDisplay?.map((item, index) => (
              <NewsListItem
                key={index}
                data={{ ...item, ...(item?.id && { isInWatchList: watchlistById[item?.id] }) }}
                type="video"
                loading={loadingRelatedVideos}
              />
            ))}
          </StyledContainer>
        </ListWrapper>
      )}
    </div>
  );
};

export default NewsPage;
const Wrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.palette.secondary.dark};
  z-index: 0;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    height: auto;
    background: ${({ theme }) => theme.palette.secondary.light};
  }
`;
const Heading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'additionalStyles',
})`
  && {
    padding: 0;
    margin: auto;
    margin-bottom: 22px;

    &:first-letter {
      text-transform: uppercase;
    }

    ${({ theme }) => theme.breakpoints.up('sm')} {
      margin-bottom: ${MIDDLE_MARGIN_PX};
    }

    ${({ theme }) => theme.breakpoints.up('ml')} {
      font-size: 40px;
    }
  }
`;
const StyledRestrictedContainer = styled('div')`
  & {
    padding: 0 16px;
  }
`;
const StyledContainer = styled(Container)`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  & > :nth-child(n + 3) {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 1440px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-wrap: nowrap;

    & > :nth-child(n + 3) {
      display: flex;
    }

    justify-content: center;
  }
`;
const ListWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDark',
})<{ isDark?: boolean }>`
  background: ${({ isDark, theme }) =>
    isDark
      ? theme.palette.common.blockBackground.main
      : theme.palette.common.blockBackground.light};
  padding: 72px 0;
`;
const Gradient = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
    at center,
    ${({ theme }) => theme.palette.common.blue} 0%,
    ${({ theme }) => theme.palette.common.primary} 100%
  );
  transition: background 0.3s, border-radius 0.3s, opacity 0.3s;
  opacity: 0.3;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    background-image: radial-gradient(
      at center left,
      ${({ theme }) => theme.palette.common.blue} 0%,
      ${({ theme }) => theme.palette.common.primary} 100%
    );
  }
`;
const TopGradient = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 300px;
  background: linear-gradient(
      180deg,
      ${({ theme }) => theme.palette.common.blueDark} 0%,
      ${({ theme }) => alpha(theme.palette.common.blueDark, 0)} 100%
    )
    0% 0% no-repeat;
  background-origin: padding-box;
`;
const BottomGradient = styled('div')`
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 373px;
  background: linear-gradient(
      0deg,
      ${({ theme }) => theme.palette.common.blueDark} 0%,
      ${({ theme }) => alpha(theme.palette.common.blueDark, 0)} 100%
    )
    0% 0% no-repeat;
  background-origin: padding-box;
`;
const BackgroundImg = styled(IKImage)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  object-fit: cover;
`;
